import { BlockNoteEditor } from '@blocknote/core';
import {
  Block,
  BlockNoteDocument,
  BlockSchema,
  EditorPartialTableContent,
} from '../schema';
import getEditorDomSearchRoot from './get-editor-dom-search-root';
import {
  findTableElementForBlock,
  listTableElementsInEditor,
  measureTableColumnWidthsCssPx,
} from './measure-table-column-widths-css-px';
import mergeTableColumnWidths, {
  tableColumnWidthsNeedSync,
} from './merge-table-column-widths';
import stripTrailingEmptyParagraphs from './strip-trailing-empty-paragraphs';
import { syncImagePreviewDimensionsInEditor } from './sync-image-preview-dimensions-in-document';

type BlockTableContent = Extract<Block['content'], { type: 'tableContent' }>;
type SyncTableColumnWidthsOptions = {
  measureAllTableWidthsFromDom?: boolean;
};
export type PrepareDocumentForPersistOptions = SyncTableColumnWidthsOptions & {
  measureAllImageDimensionsFromDom?: boolean;
};

export function tableBlockNeedsWidthSync(block: Block) {
  return (
    block.type === 'table' &&
    isTableContent(block.content) &&
    tableColumnWidthsNeedSync(block.content.columnWidths)
  );
}

export function syncTableColumnWidthsInEditor(
  editor: BlockNoteEditor<BlockSchema>,
  options: SyncTableColumnWidthsOptions = {},
) {
  const editorRoot = getEditorDomSearchRoot(editor);
  if (!editorRoot) {
    return;
  }

  const tableElementsByIndex = listTableElementsInEditor(editorRoot);
  let tableIndex = 0;

  for (const block of editor.document) {
    tableIndex = syncTableBlockColumnWidths(
      editor,
      editorRoot,
      block,
      tableElementsByIndex,
      tableIndex,
      options,
    );
  }
}

export function prepareDocumentForPersist(
  editor: BlockNoteEditor<BlockSchema>,
  document: BlockNoteDocument,
  options: PrepareDocumentForPersistOptions = {},
) {
  stripTrailingEmptyParagraphs([...document]);
  syncTableColumnWidthsInEditor(editor, options);
  syncImagePreviewDimensionsInEditor(editor, options);
  return stripTrailingEmptyParagraphs([...editor.document]);
}

function columnWidthsMatchMerged(
  stored: EditorPartialTableContent['columnWidths'],
  merged: Array<number>,
) {
  if (!stored?.length) {
    return false;
  }
  if (stored.length !== merged.length) {
    return false;
  }
  return stored.every((width, index) => {
    if (width === null || width === undefined) {
      return false;
    }
    const parsed = typeof width === 'number' ? width : NaN;
    return Number.isFinite(parsed) && Math.round(parsed) === merged[index];
  });
}

function isTableContent(
  content: Block['content'],
): content is BlockTableContent {
  return (
    typeof content === 'object' &&
    content !== null &&
    'type' in content &&
    content.type === 'tableContent'
  );
}

function syncTableBlockColumnWidths(
  editor: BlockNoteEditor<BlockSchema>,
  editorRoot: HTMLElement,
  block: Block,
  tableElementsByIndex: Array<HTMLTableElement>,
  tableIndex: number,
  options: SyncTableColumnWidthsOptions,
) {
  if (block.type !== 'table' || !isTableContent(block.content)) {
    return tableIndex;
  }

  const content = block.content;
  if (
    !options.measureAllTableWidthsFromDom &&
    !tableColumnWidthsNeedSync(content.columnWidths)
  ) {
    return tableIndex + 1;
  }

  const tableEl = findTableElementForBlock(
    editorRoot,
    block.id,
    tableElementsByIndex,
    tableIndex,
  );
  if (!tableEl) {
    return tableIndex + 1;
  }

  const measured = measureTableColumnWidthsCssPx(tableEl);
  const columnWidths = mergeTableColumnWidths(content.columnWidths, measured);

  if (columnWidthsMatchMerged(content.columnWidths, columnWidths)) {
    return tableIndex + 1;
  }

  editor.updateBlock(block, {
    type: 'table',
    content: {
      ...content,
      columnWidths,
    },
  });

  return tableIndex + 1;
}
